import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../lib/apiOrigin";
import { useAdminLanguage } from "../../i18n/adminTranslations";

const money = (cents) => (cents == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100));

export default function CatalogPricesTab({ token }) {
  const { a } = useAdminLanguage();
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const headers = { "X-Admin-Token": token };
  const load = useCallback(() => axios.get(`${API_BASE}/admin/catalog-prices`, { headers: { "X-Admin-Token": token } }).then((r) => setData(r.data)), [token]);
  useEffect(() => { load().catch(() => setMessage(a("Unable to load catalog prices."))); }, [load, a]);
  const save = async (optionId) => {
    const dollars = Number(edits[optionId]);
    if (!Number.isFinite(dollars) || dollars < 1 || busy) return;
    setBusy(true); setMessage("");
    try {
      await axios.put(`${API_BASE}/admin/catalog-prices`, { option_id: optionId, amount_cents: Math.round(dollars * 100), expected_version: data.price_version }, { headers });
      setEdits((e) => ({ ...e, [optionId]: "" })); setMessage(a("Saved as a new price version. Quotes already sent keep their original prices.")); await load();
    } catch (err) { setMessage(err?.response?.status === 409 ? a("Prices changed. Refresh before editing.") : a("Unable to save the price.")); }
    finally { setBusy(false); }
  };
  if (!data) return <p data-testid="catalog-prices-loading">{message || a("Loading…")}</p>;
  return (
    <section data-testid="catalog-prices-tab" className="space-y-5">
      <h2 className="text-xl font-bold">{a("Automatic quote catalog")}</h2>
      <p className="text-zinc-400 text-sm">{a("Known models are priced by the server and the quote is emailed immediately. PERGOLUX, BON PERGOLA and models outside this table always go to manual review. Every change creates a new auditable version; sent quotes never change.")}</p>
      <p className="text-xs text-zinc-500" data-testid="catalog-price-version">{a("Current price version")}: {data.price_version}</p>
      {message && <p role="status" data-testid="catalog-prices-message" className="text-sky-300">{message}</p>}
      {data.products.map((p) => (
        <article key={p.id} data-testid={`catalog-product-${p.id}`} className="border border-white/15 p-4 space-y-2">
          <h3 className="font-bold">{p.brand} · {p.label} {p.manual_review && <span className="ml-2 text-xs uppercase tracking-wider text-amber-300 border border-amber-400/40 px-2 py-0.5">{a("Manual review")}</span>}</h3>
          <ul className="space-y-1">
            {p.options.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center gap-3 text-sm">
                <span className="flex-1 min-w-[12rem] break-words">{o.label}</span>
                <span className="text-zinc-400">{a("Reference")}: {money(o.reference_cents)}</span>
                <span className="font-semibold" data-testid={`catalog-current-${o.id}`}>{money(o.current_cents)}</span>
                {o.reference_cents != null && !p.manual_review && <>
                  <input aria-label={`${a("New price")} ${o.label}`} data-testid={`catalog-edit-${o.id}`} type="number" min="1" step="0.01" className="w-28 bg-zinc-900 border border-white/15 p-1" value={edits[o.id] || ""} onChange={(e) => setEdits((x) => ({ ...x, [o.id]: e.target.value }))} />
                  <button data-testid={`catalog-save-${o.id}`} className="border border-sky-400 text-sky-300 px-3 py-1 disabled:opacity-40" disabled={busy || !edits[o.id]} onClick={() => save(o.id)}>{a("Save")}</button>
                </>}
              </li>
            ))}
          </ul>
        </article>
      ))}
    </section>
  );
}
